/** ⌘K command palette: jump to any v2 screen or v1 page. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { MODULES, V1_GROUPS } from "../nav";
import { Icon, type IconName } from "./Icon";

interface Entry {
  label: string;
  group: string;
  icon: IconName;
  /** extra words it is found by */
  keywords?: string;
  go: () => void;
}

export function Palette({
  onClose,
  v1Url,
  rangeQuery,
}: {
  onClose: () => void;
  v1Url: string | null;
  rangeQuery: string;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [];
    for (const m of MODULES)
      for (const g of m.groups)
        for (const i of g.items)
          out.push({
            label: i.label,
            group: m.label,
            icon: i.icon,
            keywords: i.keywords,
            go: () => navigate(`${i.to}?${rangeQuery}`),
          });
    out.push({ label: "Apps", group: "All tools", icon: "grid", go: () => navigate(`/apps?${rangeQuery}`) });
    if (v1Url)
      for (const g of V1_GROUPS)
        for (const i of g.items)
          out.push({
            label: i.label,
            group: "V1",
            icon: i.icon,
            go: () => navigate(`/v1${i.path}`),
          });
    return out;
  }, [navigate, v1Url, rangeQuery]);

  const shown = entries
    .filter((e) => `${e.label} ${e.group} ${e.keywords ?? ""}`.toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 40);

  useEffect(() => input.current?.focus(), []);

  const pick = (e: Entry | undefined) => {
    if (!e) return;
    onClose();
    e.go();
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: Escape is handled on the input; the scrim click is a mouse convenience
    <div className="scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="palette" role="dialog" aria-label="Jump to">
        <input
          ref={input}
          value={q}
          placeholder="Jump to a screen…"
          aria-label="Jump to a screen"
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") setSel((s) => Math.min(s + 1, shown.length - 1));
            else if (e.key === "ArrowUp") setSel((s) => Math.max(s - 1, 0));
            else if (e.key === "Enter") pick(shown[sel]);
            else if (e.key === "Escape") onClose();
            else return;
            e.preventDefault();
          }}
        />
        <div className="res" role="listbox">
          {shown.length ? (
            shown.map((e, i) => (
              <button
                type="button"
                role="option"
                aria-selected={i === sel}
                key={`${e.group}-${e.label}`}
                className={`it${i === sel ? " on" : ""}`}
                onMouseEnter={() => setSel(i)}
                onClick={() => pick(e)}
              >
                <Icon name={e.icon} />
                {e.label}
                <span className="k muted">{e.group}</span>
              </button>
            ))
          ) : (
            <div className="empty">No screen matches.</div>
          )}
        </div>
      </div>
    </div>
  );
}
